/**
 * Small UPS edit body for the descriptor engine (#997 Story 6.4, mirror of
 * `RacksInstanceEditFormBody`).
 *
 * Winston's ADR (parity by identity): the engine renders the EXISTING bespoke
 * `SmallUpsEditFormContent` — the user-approved Small UPS editor (field set,
 * order, labels, placeholders, 3 dropdowns / NumberInput steppers / Y-N choice,
 * both per-unit attachment carousels, Additional-Info card) — bound to the
 * engine's edit-session state. The engine owns list/save/flush/attachment
 * lifecycle (the 6.1/6.2 fixes), the section owns its view.
 *
 * Binding contract:
 * - `formData` is SMALL-UPS-shaped: the engine's editor field map (numbers as
 *   text, yesNo as boolean) is coerced back through `descriptorFieldMapToPatch`,
 *   plus identity (`name` ← `instanceName`, `status` ← `instanceStatus`) and the
 *   unit id (`id` ← `configurationId`, which `SmallUpsEditFormContent` uses to
 *   scope its two per-unit attachment cards — 6.1).
 * - `updateField` routes identity keys to the engine identity setters and every
 *   data field to `setInstanceField`, converting bespoke Small-UPS-typed values
 *   to the engine's editor forms (number → text, undefined → '', boolean kept).
 *
 * GATE-4 (deferred-work.md:494): `descriptorFieldMapToPatch` THROWS on unmapped
 * keys. `SmallUpsEditFormContent` only writes identity + the descriptor
 * per-unit fields, so no unmapped key ever reaches the patch.
 */

import React, { useCallback, useMemo } from 'react';

import { SmallUpsEditFormContent } from '../../../../components/smallups/SmallUpsEditFormContent';
import { normalizeSmallUpsStatus } from '../../../../components/smallups/smallUpsListUtils';
import { descriptorFieldMapToPatch } from '../../../sections/descriptorEngineAdapter';
import { SMALL_UPS_DESCRIPTOR } from '../../../sections/smallUps.descriptor';

import type { RepeatableInstanceEditFormRenderProps } from '../../../../components/repeatableSections/repeatableInstanceEditFormTypes';
import type { SmallUpsConfiguration } from '../../../../types/smallUps';

export function SmallUpsEngineEditFormBody({
  instanceFields,
  setInstanceField,
  instanceName,
  instanceStatus,
  setInstanceName,
  setInstanceStatus,
  disabled,
  requestStage,
  requestId,
  configurationId,
  attachmentsBySection,
  onAttachFiles,
  onDeleteAttachment,
  selectedFiles,
  onFilesSelected,
  offlineMode,
  onCaptionChange,
  pendingCaptionEdits,
  markDirty,
}: RepeatableInstanceEditFormRenderProps): React.ReactElement {
  const toPatch = useMemo(
    () => descriptorFieldMapToPatch(SMALL_UPS_DESCRIPTOR),
    [],
  );

  const formData = useMemo((): Partial<SmallUpsConfiguration> => {
    const {
      instanceName: patchName,
      instanceStatus: patchStatus,
      ...dataFields
    } = toPatch(instanceFields);
    return {
      ...(dataFields as Partial<SmallUpsConfiguration>),
      id: configurationId,
      name: instanceName ?? (patchName as string | undefined),
      status: normalizeSmallUpsStatus(
        instanceStatus ?? (patchStatus as string | undefined),
      ),
    };
  }, [toPatch, instanceFields, configurationId, instanceName, instanceStatus]);

  const updateField = useCallback(
    <K extends keyof SmallUpsConfiguration>(
      field: K,
      value: SmallUpsConfiguration[K],
    ) => {
      if (field === 'name') {
        const name = typeof value === 'string' ? value : '';
        if (setInstanceName) {
          setInstanceName(name);
        } else {
          setInstanceField('instanceName', name);
        }
        return;
      }
      if (field === 'status') {
        const status = normalizeSmallUpsStatus(
          typeof value === 'string' ? value : undefined,
        );
        if (setInstanceStatus) {
          setInstanceStatus(status);
        } else {
          setInstanceField('instanceStatus', status);
        }
        return;
      }
      // The engine session stores editor forms: booleans stay booleans (yesNo),
      // numbers become text, a cleared number becomes blank — never 'undefined'.
      const editorValue =
        typeof value === 'boolean' ? value : value == null ? '' : String(value);
      setInstanceField(field as string, editorValue);
    },
    [setInstanceField, setInstanceName, setInstanceStatus],
  );

  const onFilesSelectedDirty = useMemo(
    () =>
      onFilesSelected
        ? (sectionId: string, files: Parameters<typeof onFilesSelected>[1]) => {
            markDirty();
            onFilesSelected(sectionId, files);
          }
        : undefined,
    [markDirty, onFilesSelected],
  );

  return (
    <SmallUpsEditFormContent
      formData={formData}
      updateField={updateField}
      disabled={disabled}
      requestStage={requestStage}
      requestId={requestId}
      attachmentsBySection={attachmentsBySection}
      onAttachFiles={onAttachFiles}
      onDeleteAttachment={onDeleteAttachment}
      selectedFiles={selectedFiles}
      onFilesSelected={onFilesSelectedDirty}
      isOffline={offlineMode}
      onCaptionChange={onCaptionChange}
      pendingCaptionEdits={pendingCaptionEdits}
    />
  );
}
